import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, Users, Key, Shield, Database, Crown, UserCog, Activity, TrendingUp, FileText, BookOpen, GraduationCap, Clock } from 'lucide-react';
import { motion } from 'motion/react';
import APIKeyManager from './APIKeyManager';
import UserTierManager from './UserTierManager';
import SuperAdminAssigner from './SuperAdminAssigner';
import AdminUserManager from '@/components/admin/AdminUserManager';
import TrialStatusFixer from '@/components/admin/TrialStatusFixer';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface SystemStats {
  totalUsers: number;
  totalAdmins: number;
  totalSuperAdmins: number;
  totalClasses: number;
  totalPresentations: number;
  totalQuizzes: number;
  totalLessonPlans: number;
  recentActivities: ActivityItem[];
}

interface ActivityItem {
  id: string;
  type: 'user_registration' | 'lesson_plan' | 'quiz_created' | 'presentation_created' | 'admin_assigned';
  description: string;
  timestamp: string;
  user_id?: number;
  user_name?: string;
}

const SuperAdminDashboard = () => {
  const { user, isSuperAdmin, adminStatus } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState<SystemStats>({
    totalUsers: 0,
    totalAdmins: 0,
    totalSuperAdmins: 0,
    totalClasses: 0,
    totalPresentations: 0,
    totalQuizzes: 0,
    totalLessonPlans: 0,
    recentActivities: []
  });
  const [isLoading, setIsLoading] = useState(true);

  // Check if user has admin access (AdminL1, AdminL2, or SuperAdmin)
  const hasAdminAccess = isSuperAdmin || adminStatus.is_admin;

  useEffect(() => {
    loadSystemStats();

    // Set up a retry mechanism if APIs aren't available initially
    const retryTimer = setTimeout(() => {
      if (isLoading && window.ezsite?.apis?.tablePage) {
        console.log('APIs now available, retrying system stats load');
        loadSystemStats();
      }
    }, 2000);

    return () => clearTimeout(retryTimer);
  }, []);

  const loadSystemStats = async () => {
    try {
      // Check if APIs are available
      if (!window.ezsite?.apis?.tablePage) {
        console.log('APIs not yet available, skipping system stats load');
        setIsLoading(false);
        return;
      }

      await Promise.all([
      loadUserStats(),
      loadAdminStats(),
      loadSuperAdminStats(),
      loadClassStats(),
      loadPresentationStats(),
      loadQuizStats(),
      loadLessonPlanStats(),
      loadRecentActivity()]
      );
    } catch (error) {
      console.error('Error loading system stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load system statistics',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserStats = async () => {
    try {
      // Get total users count from user_tiers table
      const response = await window.ezsite.apis.tablePage('15216', {
        PageNo: 1,
        PageSize: 1,
        OrderByField: 'assigned_at',
        IsAsc: false
      });

      if (response.error) {
        console.error('Error loading user stats:', response.error);
        return;
      }

      setStats((prev) => ({
        ...prev,
        totalUsers: response.data?.VirtualCount || 0
      }));
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  };

  const loadAdminStats = async () => {
    try {
      // Get admin count from admin_users table
      const response = await window.ezsite.apis.tablePage('15992', {
        PageNo: 1,
        PageSize: 1,
        OrderByField: 'assigned_at',
        IsAsc: false,
        Filters: [{
          name: 'is_active',
          op: 'Equal',
          value: true
        }]
      });

      if (response.error) {
        console.error('Error loading admin stats:', response.error);
        return;
      }

      setStats((prev) => ({
        ...prev,
        totalAdmins: response.data?.VirtualCount || 0
      }));
    } catch (error) {
      console.error('Error loading admin stats:', error);
    }
  };

  const loadSuperAdminStats = async () => {
    try {
      // Get superadmin count from app_settings table
      const response = await window.ezsite.apis.tablePage('15215', {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'updated_at',
        IsAsc: false,
        Filters: [{
          name: 'setting_key',
          op: 'StringStartsWith',
          value: 'superadmin_'
        }]
      });

      if (response.error) {
        console.error('Error loading superadmin stats:', response.error);
        return;
      }

      // Count hardcoded superadmins + dynamic ones
      const dynamicSuperAdmins = response.data?.VirtualCount || 0;
      const hardcodedSuperAdmins = 3; // admin@teachai.com, admin@ezsite.ai, support@ezsite.ai

      setStats((prev) => ({
        ...prev,
        totalSuperAdmins: dynamicSuperAdmins + hardcodedSuperAdmins
      }));
    } catch (error) {
      console.error('Error loading superadmin stats:', error);
    }
  };

  const loadClassStats = async () => {
    try {
      const response = await window.ezsite.apis.tablePage('15198', {
        PageNo: 1,
        PageSize: 1,
        OrderByField: 'id',
        IsAsc: false,
        Filters: [{
          name: 'is_active',
          op: 'Equal',
          value: true
        }]
      });

      if (!response.error) {
        setStats((prev) => ({
          ...prev,
          totalClasses: response.data?.VirtualCount || 0
        }));
      }
    } catch (error) {
      console.error('Error loading class stats:', error);
    }
  };

  const loadPresentationStats = async () => {
    try {
      const response = await window.ezsite.apis.tablePage('15255', {
        PageNo: 1,
        PageSize: 1,
        OrderByField: 'created_at',
        IsAsc: false
      });

      if (!response.error) {
        setStats((prev) => ({
          ...prev,
          totalPresentations: response.data?.VirtualCount || 0
        }));
      }
    } catch (error) {
      console.error('Error loading presentation stats:', error);
    }
  };

  const loadQuizStats = async () => {
    try {
      const response = await window.ezsite.apis.tablePage('15204', {
        PageNo: 1,
        PageSize: 1,
        OrderByField: 'id',
        IsAsc: false
      });

      if (!response.error) {
        setStats((prev) => ({
          ...prev,
          totalQuizzes: response.data?.VirtualCount || 0
        }));
      }
    } catch (error) {
      console.error('Error loading quiz stats:', error);
    }
  };

  const loadLessonPlanStats = async () => {
    try {
      const response = await window.ezsite.apis.tablePage('15206', {
        PageNo: 1,
        PageSize: 1,
        OrderByField: 'id',
        IsAsc: false
      });

      if (!response.error) {
        setStats((prev) => ({
          ...prev,
          totalLessonPlans: response.data?.VirtualCount || 0
        }));
      }
    } catch (error) {
      console.error('Error loading lesson plan stats:', error);
    }
  };

  const loadRecentActivity = async () => {
    try {
      const activities: ActivityItem[] = [];

      // Get recent presentations
      const presentationsResponse = await window.ezsite.apis.tablePage('15255', {
        PageNo: 1,
        PageSize: 3,
        OrderByField: 'created_at',
        IsAsc: false
      });

      if (!presentationsResponse.error && presentationsResponse.data?.List) {
        presentationsResponse.data.List.forEach((presentation: any) => {
          activities.push({
            id: `presentation-${presentation.id}`,
            type: 'presentation_created',
            description: `Presentation "${presentation.title}" created`,
            timestamp: presentation.created_at,
            user_id: presentation.teacher_id
          });
        });
      }

      // Get recent quizzes
      const quizzesResponse = await window.ezsite.apis.tablePage('15204', {
        PageNo: 1,
        PageSize: 3,
        OrderByField: 'id',
        IsAsc: false
      });

      if (!quizzesResponse.error && quizzesResponse.data?.List) {
        quizzesResponse.data.List.forEach((quiz: any) => {
          activities.push({
            id: `quiz-${quiz.id}`,
            type: 'quiz_created',
            description: `Quiz "${quiz.title}" created`,
            timestamp: new Date().toISOString(), // Fallback timestamp
            user_id: quiz.teacher_id
          });
        });
      }

      // Get recent lesson plans
      const lessonPlansResponse = await window.ezsite.apis.tablePage('15206', {
        PageNo: 1,
        PageSize: 3,
        OrderByField: 'id',
        IsAsc: false
      });

      if (!lessonPlansResponse.error && lessonPlansResponse.data?.List) {
        lessonPlansResponse.data.List.forEach((lessonPlan: any) => {
          activities.push({
            id: `lesson-${lessonPlan.id}`,
            type: 'lesson_plan',
            description: `Lesson plan "${lessonPlan.title}" created`,
            timestamp: new Date().toISOString(), // Fallback timestamp
            user_id: lessonPlan.teacher_id
          });
        });
      }

      // Sort activities by timestamp (newest first)
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      setStats((prev) => ({
        ...prev,
        recentActivities: activities.slice(0, 5) // Keep only top 5
      }));
    } catch (error) {
      console.error('Error loading recent activity:', error);
    }
  };

  const statCards = [
  {
    title: 'Total Users',
    value: isLoading ? '...' : stats.totalUsers.toString(),
    icon: Users,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50'
  },
  {
    title: 'Admin Users',
    value: isLoading ? '...' : stats.totalAdmins.toString(),
    icon: UserCog,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50'
  },
  {
    title: 'SuperAdmins',
    value: isLoading ? '...' : stats.totalSuperAdmins.toString(),
    icon: Crown,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50'
  },
  {
    title: 'Active Classes',
    value: isLoading ? '...' : stats.totalClasses.toString(),
    icon: GraduationCap,
    color: 'text-green-600',
    bgColor: 'bg-green-50'
  },
  {
    title: 'Presentations',
    value: isLoading ? '...' : stats.totalPresentations.toString(),
    icon: FileText,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50'
  },
  {
    title: 'Quizzes Created',
    value: isLoading ? '...' : stats.totalQuizzes.toString(),
    icon: BookOpen,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50'
  },
  {
    title: 'Lesson Plans',
    value: isLoading ? '...' : stats.totalLessonPlans.toString(),
    icon: Database,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50'
  },
  {
    title: 'System Health',
    value: 'Operational',
    icon: Shield,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50'
  }];


  // Get the appropriate title and badge based on user type
  const getDashboardTitle = () => {
    if (isSuperAdmin) return 'Super Admin Dashboard';
    if (adminStatus.admin_level === 2) return 'Level 2 Admin Dashboard';
    return 'Admin Dashboard';
  };

  const getBadgeInfo = () => {
    if (isSuperAdmin) {
      return {
        label: 'Super Admin',
        className: 'bg-red-50 text-red-700 border-red-200',
        icon: Shield
      };
    }
    if (adminStatus.admin_level === 2) {
      return {
        label: 'Admin L2',
        className: 'bg-orange-50 text-orange-700 border-orange-200',
        icon: UserCog
      };
    }
    return {
      label: 'Admin L1',
      className: 'bg-purple-50 text-purple-700 border-purple-200',
      icon: UserCog
    };
  };

  const badgeInfo = getBadgeInfo();
  const BadgeIcon = badgeInfo.icon;

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'presentation_created':
        return FileText;
      case 'quiz_created':
        return BookOpen;
      case 'lesson_plan':
        return Database;
      case 'user_registration':
        return Users;
      case 'admin_assigned':
        return UserCog;
      default:
        return Activity;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-id="m1ddq8x7v" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8" data-id="j3mub1gg8" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
        <div className="flex items-center justify-between" data-id="agst8r15a" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
          <div data-id="eclo2da61" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
            <h1 className="text-3xl font-bold text-gray-900" data-id="ppe0seh93" data-path="src/components/superadmin/SuperAdminDashboard.tsx">{getDashboardTitle()}</h1>
            <p className="text-gray-600 mt-2" data-id="3crfvksu0" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
              {isSuperAdmin ?
              'Manage all system settings and user access' :
              'Manage users, API settings, and system administration'}
            </p>
          </div>
          <Badge variant="outline" className={badgeInfo.className} data-id="sguk4uf64" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
            <BadgeIcon className="w-4 h-4 mr-1" data-id="156wz8529" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
            {badgeInfo.label}
          </Badge>
        </div>
      </motion.div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" data-id="qgus43qi9" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
        <TabsList className="grid w-full grid-cols-6" data-id="2ql219k3a" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
          <TabsTrigger value="overview" data-id="eta9h0n7w" data-path="src/components/superadmin/SuperAdminDashboard.tsx">Overview</TabsTrigger>
          <TabsTrigger value="admins" data-id="0idncywy4" data-path="src/components/superadmin/SuperAdminDashboard.tsx">Admin Users</TabsTrigger>
          {/* SuperAdmin assignment - only available to SuperAdmins */}
          {isSuperAdmin && <TabsTrigger value="superadmins" data-id="x2kmsnn50" data-path="src/components/superadmin/SuperAdminDashboard.tsx">SuperAdmins</TabsTrigger>}
          {!isSuperAdmin && <TabsTrigger value="superadmins" disabled data-id="73x4nmgsi" data-path="src/components/superadmin/SuperAdminDashboard.tsx">SuperAdmins</TabsTrigger>}
          {/* API Keys and User Tiers - now available to all admin levels */}
          <TabsTrigger value="api-keys" data-id="l2ja8dsgf" data-path="src/components/superadmin/SuperAdminDashboard.tsx">API Management</TabsTrigger>
          <TabsTrigger value="user-tiers" data-id="ravbro1rb" data-path="src/components/superadmin/SuperAdminDashboard.tsx">User Tiers</TabsTrigger>
          <TabsTrigger value="trial-fixer" data-id="f5hz0ob41" data-path="src/components/superadmin/SuperAdminDashboard.tsx">Trial Fixer</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6" data-id="3tlk2l192" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="gqh7v63ly" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card key={stat.title} data-id="r3i69abgs" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                  <CardContent className="p-6" data-id="4cul04rpb" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                    <div className="flex items-center justify-between" data-id="cp32fr3az" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                      <div data-id="hz2ooz2iz" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                        <p className="text-sm font-medium text-gray-600" data-id="8imef1pr4" data-path="src/components/superadmin/SuperAdminDashboard.tsx">{stat.title}</p>
                        <p className="text-2xl font-bold text-gray-900" data-id="ua41ypowe" data-path="src/components/superadmin/SuperAdminDashboard.tsx">{stat.value}</p>
                      </div>
                      <div className={`p-3 rounded-full ${stat.bgColor}`} data-id="tr94ixagz" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                        <Icon className={`w-6 h-6 ${stat.color}`} data-id="fa8fz3qek" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
                      </div>
                    </div>
                  </CardContent>
                </Card>);

            })}
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="nbfs26l6r" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
            <Card data-id="70hyk5dx7" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
              <CardHeader data-id="rpdjyg9yr" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                <CardTitle className="flex items-center gap-2" data-id="fk38f3yho" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                  <Settings className="w-5 h-5" data-id="pmmiun8g9" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
                  System Configuration
                </CardTitle>
                <CardDescription data-id="kdopb19t4" data-path="src/components/superadmin/SuperAdminDashboard.tsx">Manage global system settings and configurations</CardDescription>
              </CardHeader>
              <CardContent data-id="bac9up438" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                <div className="space-y-4" data-id="iu9eoro2x" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="2507qdeah" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                    <span className="text-sm font-medium" data-id="da10ihgj0" data-path="src/components/superadmin/SuperAdminDashboard.tsx">Gemini API</span>
                    <Badge variant="outline" className="bg-green-50 text-green-700" data-id="mvyyeu3j9" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                      Configured
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="7gpsy5bjx" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                    <span className="text-sm font-medium" data-id="g37gmfo78" data-path="src/components/superadmin/SuperAdminDashboard.tsx">User Tiers</span>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700" data-id="pz6n6ollr" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                      Active
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="6ku05x2cc" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                    <span className="text-sm font-medium" data-id="bztgycicq" data-path="src/components/superadmin/SuperAdminDashboard.tsx">Admin System</span>
                    <Badge variant="outline" className="bg-purple-50 text-purple-700" data-id="2rtlztat7" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                      Enhanced
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-id="co28ntlgc" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
              <CardHeader data-id="sn7o96pnm" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                <CardTitle className="flex items-center gap-2" data-id="tjyl9vuqe" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                  <Activity className="w-5 h-5" data-id="mx5zprgn3" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
                  Recent Activity
                </CardTitle>
                <CardDescription data-id="kgds61pqv" data-path="src/components/superadmin/SuperAdminDashboard.tsx">Latest platform activity and usage</CardDescription>
              </CardHeader>
              <CardContent data-id="hcqeg3xkw" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                <div className="space-y-3" data-id="yn9gzcof4" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                  {stats.recentActivities.length > 0 ?
                  stats.recentActivities.map((activity) => {
                    const ActivityIcon = getActivityIcon(activity.type);
                    return (
                      <div key={activity.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg" data-id="wqs7jh736" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                          <div className="p-2 bg-blue-50 rounded-full" data-id="3tq7xbxnl" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                            <ActivityIcon className="w-4 h-4 text-blue-600" data-id="vxz863hu4" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="7i4zfghta" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                            <p className="text-sm font-medium text-gray-900 truncate" data-id="1fw50wm25" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                              {activity.description}
                            </p>
                            <p className="text-xs text-gray-500" data-id="r4sj06qc3" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                              {new Date(activity.timestamp).toLocaleDateString()} at {new Date(activity.timestamp).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>);

                  }) :

                  <div className="text-center py-4 text-gray-500" data-id="9gzqqvpjs" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                      <Clock className="w-8 h-8 mx-auto mb-2 text-gray-400" data-id="m8htj282p" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
                      <p className="text-sm" data-id="mfiq86qyq" data-path="src/components/superadmin/SuperAdminDashboard.tsx">No recent activity to display</p>
                    </div>
                  }
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card data-id="e1dyezig3" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
            <CardHeader data-id="08wrmemvu" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
              <CardTitle className="flex items-center gap-2" data-id="hyh4smsrm" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                <TrendingUp className="w-5 h-5" data-id="uh2eqsg3g" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
                Quick Actions
              </CardTitle>
              <CardDescription data-id="kqfhx3uld" data-path="src/components/superadmin/SuperAdminDashboard.tsx">Common administrative tasks</CardDescription>
            </CardHeader>
            <CardContent data-id="3q0oyougl" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-id="tcaja6t8t" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                <Button variant="outline" className="justify-start" onClick={() => setActiveTab('admins')} data-id="a4gluqea1" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                  <UserCog className="w-4 h-4 mr-2" data-id="wti0xjkhx" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
                  Manage Admin Users
                </Button>
                {isSuperAdmin &&
                <Button
                  variant="outline"
                  className="justify-start"
                  onClick={() => setActiveTab('superadmins')} data-id="v2fwy4ked" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                    <Crown className="w-4 h-4 mr-2" data-id="93nrn3sjx" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
                    Manage SuperAdmins
                  </Button>
                }
                <Button variant="outline" className="justify-start" onClick={() => setActiveTab('api-keys')} data-id="a004zyxkq" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                  <Key className="w-4 h-4 mr-2" data-id="b4o84qmls" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
                  Manage API Keys
                </Button>
                <Button
                  variant="outline"
                  className="justify-start"
                  onClick={() => setActiveTab('user-tiers')} data-id="96ubuxiaa" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
                  <Shield className="w-4 h-4 mr-2" data-id="wy3uce31n" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
                  Manage User Tiers
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="admins" data-id="mm93tqbi6" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
          <AdminUserManager data-id="8l6pftz1k" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
        </TabsContent>

        {isSuperAdmin &&
        <TabsContent value="superadmins" data-id="1qqim8fzl" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
            <SuperAdminAssigner data-id="wtfnazuwb" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
          </TabsContent>
        }

        <TabsContent value="api-keys" data-id="8xa06wbxv" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
          <APIKeyManager data-id="jnahxw1ds" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
        </TabsContent>

        <TabsContent value="user-tiers" data-id="ng4730d4b" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
          <UserTierManager data-id="f3yddagcq" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
        </TabsContent>

        <TabsContent value="trial-fixer" data-id="r65s0jle4" data-path="src/components/superadmin/SuperAdminDashboard.tsx">
          <TrialStatusFixer data-id="k5l2fqz9q" data-path="src/components/superadmin/SuperAdminDashboard.tsx" />
        </TabsContent>
      </Tabs>
    </div>);

};

export default SuperAdminDashboard;